import classnames from 'classnames';
import type React from 'react';
import { useMemo } from 'react';
import ProductModal from '~/components/modal/modals/ProductModal/ProductModal';
import { useFeatureFLags, useViaplayBruddFeatures } from '../../../../../../hooks/useFeatureFlags';
import type { SelectableItem } from '../../../../../../models/CampaignModel';
import { ProductType } from '../../../../../../models/product/ProductType';
import useModal from '../../../../../modal/Modal';
import { Button, DefaultBlockContent } from '../../../../../shared';
import { SelectedIcon } from '../components/selection-icon/SelectedIcon';
import ProductCardHat from './ProductCardHat';
import ProductCardImage from './ProductCardImage';
import ProductPrice from './ProductPrice';
import { H4 } from '@rikstv/shared-components';

interface Props {
  product: SelectableItem;
  toggleProductSelection: (product: SelectableItem) => void;
  shouldFocus: boolean;
  isSelected: boolean;
  isOpen: boolean;
}

const ProductCardFace: React.FC<Props> = ({ shouldFocus, product, toggleProductSelection, isSelected, isOpen }) => {
  const {
    displayData: { name, description, shortDescription, backgroundImageUrl, infoTag, optionalPriceTitle },
    productType,
    productKey,
  } = product;
  const cardClass = classnames('card-face', productType, { 'is-selected': isSelected, 'with-background': !!backgroundImageUrl });

  const { open } = useModal();

  const aria = {
    'aria-label': isSelected ? `${name} lagt til i handlekurv, trykk igjen for å angre` : `Legg til ${name} i handlekurv`,
    'aria-expanded': productType === ProductType.Receiver || productType === ProductType.Equipment ? isOpen : undefined,
  };

  // const cardHeaderClass =
  //   product.productType === ProductType.Installation || product.productType === ProductType.Equipment ? 'card-header-flex' : 'card-header-flex';
  // const conflictingProductNames = product.displayData.conflictingProducts?.map(({ name }) => name) ?? [];
  // const hasConflictingProducts = conflictingProductNames.length > 0;
  
  const isViaplayBrudd = useViaplayBruddFeatures(useFeatureFLags());
  const isDisabledViaplayProduct = useMemo(() => {
    return isViaplayBrudd && /viaplay|^v\s/i.test(product.displayData.name);
  }, [isViaplayBrudd, product.displayData.name]);

  return (
    <div className={cardClass} data-testid={productKey ?? name.toLowerCase()}>
      <ProductCardHat product={product} disabled={isDisabledViaplayProduct} />

      {isSelected && <SelectedIcon />}

      <div className="card-content">
        <div className={'card-header-flex'}>
          <ProductCardImage product={product} />

          <H4 className="title-4 card-name">
            {isSelected && <span className="sr-only">Valgt: </span>}
            {name}
          </H4>
        </div>

        {shortDescription && (
          <div className="sub-body">
            <DefaultBlockContent blocks={shortDescription} />
          </div>
        )}

        {infoTag && <p className="metadata card-infotag-text">{infoTag}</p>}
        {/* {hasConflictingProducts && (
          <p className="metadata card-infotag-text">Kan ikke kombineres med {formatList(conflictingProductNames, 'disjunction')}</p>
        )} */}

        <ProductPrice pricing={product.pricing} receiver={productType === ProductType.Receiver} optionalPriceTitle={optionalPriceTitle} />
      </div>

      <div className="card-footer">
        {description && (
          <Button
            type="tertiary"
            onClick={() => open(<ProductModal product={product} toggleProductSelection={toggleProductSelection} isSelected={isSelected} />)}
            aria-label={`Les mer om ${name}`}
            data-track-id="card-button"
            data-track-card-name={name}
          >
            Les mer
          </Button>
        )}
        <Button
          type="secondary"
          disabled={isDisabledViaplayProduct}
          data-testid={isSelected ? `remove ${name.toLowerCase()}` : `add ${name.toLowerCase()}`}
          focusOnRender={shouldFocus}
          onClick={() => toggleProductSelection(product)}
          {...aria}
        >
          {isSelected ? 'Angre' : 'Legg til'}
        </Button>
      </div>
    </div>
  );
};

export default ProductCardFace;
